(() => {
  'use strict';

  const CARD_SELECTOR = '.m-product-card';

  const DELIVERY_SELECTOR = [
    '[data-tanatel-delivery]',
    '.tanatel-card-delivery-compact',
    '.product-delivery-time',
    '.product-card-delivery',
    '.tanatel-card-delivery',
    '.tanatel-delivery-line'
  ].join(',');

  function normalizeText(value) {
    return String(value || '')
      .replace(/\s+/g, ' ')
      .replace(/:/g, '')
      .trim()
      .toLowerCase();
  }

  function looksLikeDelivery(node) {
    const text = normalizeText(node.textContent);
    return (
      text.includes('التسليم') ||
      text.includes('delivery') ||
      node.matches(DELIVERY_SELECTOR)
    );
  }

  function cleanCard(card) {
    if (!card) return;

    const preferred =
      card.querySelector('[data-tanatel-delivery]') ||
      card.querySelector('.tanatel-card-delivery-compact');

    if (!preferred) return;

    /* Keep the new compact delivery row. Remove any old delivery row
       rendered by legacy product-card code. */
    const candidates = Array.from(card.querySelectorAll(DELIVERY_SELECTOR));

    candidates.forEach((node) => {
      if (node === preferred) return;
      if (node.contains(preferred)) return;
      if (preferred.contains(node)) return;

      if (looksLikeDelivery(node)) {
        node.remove();
      }
    });

    /* Some legacy versions rendered the delivery row as a plain inner div
       inside the product info without a reliable class. Remove only a sibling
       that clearly contains delivery text and appears near our preferred row. */
    const info = preferred.closest('.m-product-card__info') || preferred.parentElement;
    if (info) {
      Array.from(info.children).forEach((child) => {
        if (child === preferred) return;
        if (child.classList.contains('tanatel-card-warranty')) return;
        if (child.classList.contains('gold-warranty')) return;

        const text = normalizeText(child.textContent);
        const isLegacyDelivery =
          (text.includes('التسليم خلال') || text.includes('delivery in') || text.includes('delivery within')) &&
          !child.querySelector('[data-tanatel-delivery]');

        if (isLegacyDelivery) child.remove();
      });
    }

    /* Keep one warranty only. */
    const warranties = Array.from(
      card.querySelectorAll('.gold-warranty, .tanatel-card-warranty')
    );

    if (warranties.length > 1) {
      const keep =
        warranties.find((node) => node.classList.contains('tanatel-card-warranty')) ||
        warranties[0];

      warranties.forEach((node) => {
        if (node !== keep && !keep.contains(node)) node.remove();
      });
    }
  }

  function clean(root = document) {
    if (root.matches?.(CARD_SELECTOR)) cleanCard(root);
    root.querySelectorAll?.(CARD_SELECTOR).forEach(cleanCard);
  }

  const observer = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (!(node instanceof Element)) continue;

        if (node.matches(CARD_SELECTOR)) cleanCard(node);
        node.querySelectorAll?.(CARD_SELECTOR).forEach(cleanCard);

        const parentCard = node.closest?.(CARD_SELECTOR);
        if (parentCard) cleanCard(parentCard);
      }
    }
  });

  const boot = () => {
    clean(document);
    observer.observe(document.body, {childList:true, subtree:true});
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, {once:true});
  } else {
    boot();
  }


  /* ==========================================================
     TANATEL — Select Options Modal
     Multi-variant product cards only.
     ========================================================== */
  const variantModalState = {
    product: null,
    selected: [],
    trigger: null,
    dialog: null,
  };

  const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (char) => ({
      '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#039;'
    }[char]));

  const money = (cents) => {
    const themeMoneyFormat = window.MinimogSettings?.money_format;

    if (typeof window.formatMoney === 'function' && themeMoneyFormat) {
      return window.formatMoney(Number(cents) || 0, themeMoneyFormat);
    }

    return `${((Number(cents) || 0) / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ⃁`;
  };

  const getVariant = () => {
    const {product, selected} = variantModalState;
    if (!product) return null;
    return product.variants.find((variant) =>
      variant.options.every((value, index) => value === selected[index])
    ) || null;
  };

  const closeVariantModal = () => {
    const dialog = variantModalState.dialog;
    if (!dialog) return;
    dialog.classList.remove('is-open');
    document.documentElement.classList.remove('tanatel-variant-modal-open');
    window.setTimeout(() => {
      dialog.remove();
      variantModalState.dialog = null;
      variantModalState.product = null;
      variantModalState.selected = [];
      variantModalState.trigger?.focus?.();
      variantModalState.trigger = null;
    }, 180);
  };

  const updateVariantModal = () => {
    const dialog = variantModalState.dialog;
    const product = variantModalState.product;
    if (!dialog || !product) return;

    const variant = getVariant();
    const price = dialog.querySelector('[data-tanatel-variant-price]');
    const compare = dialog.querySelector('[data-tanatel-variant-compare]');
    const add = dialog.querySelector('[data-tanatel-variant-add]');
    const status = dialog.querySelector('[data-tanatel-variant-status]');
    const showCompare = Boolean(
      variant?.compare_at_price && variant.compare_at_price > variant.price
    );

    if (price) {
      price.innerHTML = variant ? money(variant.price) : '—';
      price.style.color = showCompare ? 'rgb(var(--color-price-sale))' : '';
    }

    if (compare) {
      compare.hidden = !showCompare;
      compare.innerHTML = showCompare ? money(variant.compare_at_price) : '';
    }

    const available = Boolean(variant?.available);
    if (add) {
      add.disabled = !available;
      add.dataset.variantId = variant?.id || '';
      add.querySelector('span').textContent = available
        ? (document.documentElement.lang?.startsWith('ar') ? 'أضف للسلة' : 'Add to cart')
        : (document.documentElement.lang?.startsWith('ar') ? 'غير متوفر' : 'Unavailable');
    }

    if (status) {
      status.textContent = variant
        ? (available
            ? (document.documentElement.lang?.startsWith('ar') ? 'متوفر' : 'Available')
            : (document.documentElement.lang?.startsWith('ar') ? 'غير متوفر' : 'Unavailable'))
        : (document.documentElement.lang?.startsWith('ar') ? 'اختر جميع الخيارات' : 'Select all options');
      status.dataset.available = available ? 'true' : 'false';
    }

    const image =
      variant?.featured_image?.src ||
      variant?.featured_image?.url ||
      product.featured_image?.src ||
      product.featured_image?.url ||
      product.featured_image ||
      '';
    const imageNode = dialog.querySelector('[data-tanatel-variant-image]');
    if (imageNode && image) imageNode.src = image;
  };

  const renderVariantModal = (product) => {
    const isAr = document.documentElement.lang?.toLowerCase().startsWith('ar');
    const optionDefs = (product.options || []).map((option, optionIndex) => {
      const name = typeof option === 'string' ? option : (option?.name || `Option ${optionIndex + 1}`);
      const values = Array.isArray(option?.values) && option.values.length
        ? option.values
        : [...new Set((product.variants || []).map((variant) => variant.options?.[optionIndex]).filter(Boolean))];
      return {name, values};
    });

    const initialVariant =
      (product.variants || []).find((variant) => variant.available) ||
      (product.variants || [])[0] ||
      null;

    const selected = optionDefs.map((option, index) =>
      initialVariant?.options?.[index] || option.values[0] || ''
    );
    variantModalState.selected = selected;

    const optionMarkup = optionDefs.map((option, optionIndex) => {
      const values = option.values;
      return `
        <fieldset class="tanatel-variant-modal__option" data-option-index="${optionIndex}">
          <legend>${escapeHtml(option.name)}</legend>
          <div class="tanatel-variant-modal__choices">
            ${values.map((value) => `
              <button
                type="button"
                class="tanatel-variant-modal__choice${selected[optionIndex] === value ? ' is-selected' : ''}"
                data-option-value="${escapeHtml(value)}"
                aria-pressed="${selected[optionIndex] === value ? 'true' : 'false'}"
              >${escapeHtml(value)}</button>
            `).join('')}
          </div>
        </fieldset>`;
    }).join('');

    const wrapper = document.createElement('div');
    wrapper.className = 'tanatel-variant-modal';
    wrapper.innerHTML = `
      <div class="tanatel-variant-modal__backdrop" data-tanatel-variant-close></div>
      <div class="tanatel-variant-modal__dialog" role="dialog" aria-modal="true" aria-label="${isAr ? 'حدد خيارات المنتج' : 'Select product options'}">
        <button type="button" class="tanatel-variant-modal__close" data-tanatel-variant-close aria-label="${isAr ? 'إغلاق' : 'Close'}">×</button>
        <div class="tanatel-variant-modal__media">
          <img
            data-tanatel-variant-image
            src="${escapeHtml(product.featured_image?.src || product.featured_image?.url || product.featured_image || '')}"
            alt="${escapeHtml(product.title)}"
          >
        </div>
        <div class="tanatel-variant-modal__content">
          <div class="tanatel-variant-modal__eyebrow">${isAr ? 'حدد خياراتك' : 'Choose your options'}</div>
          <h2>${escapeHtml(product.title)}</h2>
          <div class="tanatel-variant-modal__price">
            <span data-tanatel-variant-compare hidden></span>
            <strong data-tanatel-variant-price></strong>
          </div>
          <div class="tanatel-variant-modal__options">${optionMarkup}</div>
          <div class="tanatel-variant-modal__status" data-tanatel-variant-status></div>
          <div class="tanatel-variant-modal__footer">
            <div class="tanatel-variant-modal__quantity">
              <button type="button" data-qty-minus aria-label="${isAr ? 'تقليل الكمية' : 'Decrease quantity'}">−</button>
              <input type="number" min="1" value="1" inputmode="numeric" data-tanatel-variant-qty aria-label="${isAr ? 'الكمية' : 'Quantity'}">
              <button type="button" data-qty-plus aria-label="${isAr ? 'زيادة الكمية' : 'Increase quantity'}">+</button>
            </div>
            <button type="button" class="m-button m-button--primary tanatel-variant-modal__add" data-tanatel-variant-add>
              <span>${isAr ? 'أضف للسلة' : 'Add to cart'}</span>
            </button>
          </div>
          <p class="tanatel-variant-modal__message" data-tanatel-variant-message aria-live="polite"></p>
        </div>
      </div>`;

    document.body.appendChild(wrapper);
    variantModalState.dialog = wrapper;
    document.documentElement.classList.add('tanatel-variant-modal-open');
    requestAnimationFrame(() => wrapper.classList.add('is-open'));

    wrapper.querySelectorAll('[data-tanatel-variant-close]').forEach((el) =>
      el.addEventListener('click', closeVariantModal)
    );

    wrapper.querySelectorAll('[data-option-index]').forEach((fieldset) => {
      const optionIndex = Number(fieldset.dataset.optionIndex);
      fieldset.addEventListener('click', (event) => {
        const button = event.target.closest('[data-option-value]');
        if (!button) return;
        variantModalState.selected[optionIndex] = button.dataset.optionValue;
        fieldset.querySelectorAll('[data-option-value]').forEach((item) => {
          const active = item === button;
          item.classList.toggle('is-selected', active);
          item.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        updateVariantModal();
      });
    });

    const qty = wrapper.querySelector('[data-tanatel-variant-qty]');
    wrapper.querySelector('[data-qty-minus]')?.addEventListener('click', () => {
      qty.value = Math.max(1, Number(qty.value || 1) - 1);
    });
    wrapper.querySelector('[data-qty-plus]')?.addEventListener('click', () => {
      qty.value = Math.max(1, Number(qty.value || 1) + 1);
    });

    wrapper.querySelector('[data-tanatel-variant-add]')?.addEventListener('click', async (event) => {
      const button = event.currentTarget;
      const variant = getVariant();
      const message = wrapper.querySelector('[data-tanatel-variant-message]');
      if (!variant?.available || button.disabled) return;

      button.disabled = true;
      button.classList.add('is-loading');
      if (message) message.textContent = '';

      try {
        const sections = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:grouped-sections', {
          bubbles:true,
          detail:{sections},
        }));

        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method:'POST',
          headers:{'Content-Type':'application/json','Accept':'application/json'},
          body:JSON.stringify({
            items:[{
              id:variant.id,
              quantity:Math.max(1, Number(qty?.value || 1)),
            }],
            sections:[...new Set(sections)],
            sections_url:window.location.pathname,
          }),
        });

        const result = await response.json();
        if (!response.ok) throw new Error(result?.description || result?.message || 'Cart error');

        if (message) {
          message.textContent = isAr ? 'تمت الإضافة للسلة بنجاح' : 'Added to cart successfully';
          message.dataset.success = 'true';
        }

        const cartUpdate = window.EnfitahTheme?.pubSubEvents?.cartUpdate;
        if (!cartUpdate || !result?.sections) {
          throw new Error('Cart sections were not returned');
        }

        window.EnfitahEvents.emit(cartUpdate, {
          cart:result,
          productVariantId:String(variant.id),
          source:'tanatel-select-options',
        });

        const cartDrawer = document.querySelector('m-cart-drawer');
        if (cartDrawer && window.EnfitahSettings?.enable_cart_drawer) {
          cartDrawer.open(button);
        }

        window.setTimeout(closeVariantModal, 700);
      } catch (error) {
        if (message) {
          message.textContent = error?.message || (isAr ? 'تعذر الإضافة للسلة' : 'Could not add to cart');
          message.dataset.success = 'false';
        }
        button.disabled = false;
      } finally {
        button.classList.remove('is-loading');
      }
    });

    wrapper.querySelector('.tanatel-variant-modal__close')?.focus();
    updateVariantModal();
  };

  const openVariantModal = async (trigger) => {
    const handle = trigger?.dataset?.productHandle;
    if (!handle) return;

    variantModalState.trigger = trigger;
    trigger.disabled = true;
    trigger.classList.add('is-loading');

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
        headers:{Accept:'application/json'},
      });
      if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
      const product = await response.json();
      variantModalState.product = product;
      renderVariantModal(product);
    } catch (error) {
      console.error('[Tanatel Select Options]', error);
      window.location.assign(trigger.dataset.productUrl || `/products/${encodeURIComponent(handle)}`);
    } finally {
      trigger.disabled = false;
      trigger.classList.remove('is-loading');
    }
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-tanatel-select-options]');
    if (!trigger) return;
    event.preventDefault();
    event.stopPropagation();
    openVariantModal(trigger);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && variantModalState.dialog) closeVariantModal();
  });

})();
